import type { ReportData } from '@vy/types';
import type { PartialNever } from '@vy/utils';
import { useQueryClient } from 'react-query';
import { VyTable } from '../../../../../../components/new-design/table/vy-table/VyTable';
import type { VyTableMinimalProps } from '../../../../../../components/new-design/table/vy-table/vy-table-types';
import { useDeleteReport } from '../../../../../../hooks/requests/use-delete-report';
import { queryKeys } from '../../../../../../services/query-key/query-keys';
import { runMutation } from '../../../../../../utils/vy-mutation';
import type { ReportJobsDownloadProps } from '../ReportJobsSection';
import { REPORT_DATA_KEYS, getReportJobsColumns } from './ReportJobsTableColumns';

type ReportJobsTableProps = (Required<ReportJobsDownloadProps> | PartialNever<ReportJobsDownloadProps>) &
  Omit<VyTableMinimalProps<ReportData>, 'tableName'>;

export const ReportJobsTable: React.FC<ReportJobsTableProps> = ({ asDownload, onDownload, ...props }) => {
  const queryClient = useQueryClient();

  const deleteReportMutation = useDeleteReport({
    onSuccess: () => queryClient.invalidateQueries(queryKeys.reports()),
  });

  const handleDelete = async (reportId: string) => {
    await runMutation(
      deleteReportMutation,
      { reportId },
      {
        pending: 'Deleting report...',
        success: 'Report successfully deleted',
        error: 'Failed to delete report',
      },
    );
  };

  const columns = getReportJobsColumns(handleDelete, onDownload);

  return (
    <VyTable
      columns={columns}
      {...props}
      tableName="report-jobs"
      getRowId={() => crypto.randomUUID()}
      initialState={{
        sorting: {
          sortModel: [{ field: REPORT_DATA_KEYS.CREATED_AT, sort: 'desc' }],
        },
      }}
      getRowClassName={() => 'group'}
      sx={(theme) => ({
        height: `calc(100vh - ${theme.spacing(22)})`,

        '& .MuiDataGrid-footerContainer': {
          borderTop: 'none',
        },
      })}
    />
  );
};
